import { useState } from "react";
import { Button } from "@/components/ui/button";
import { api, API_BASE } from "@/lib/api";
import { Download, Loader2, ShieldCheck, RotateCcw } from "lucide-react";
import { toast } from "sonner";

export default function AdminDeployment() {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/admin/deployment-package", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const today = new Date().toISOString().slice(0, 10);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tara-finserv-deployment-${today}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Deployment package downloaded");
    } catch (e) {
      toast.error(`Download failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="admin-deployment-page">
      <div className="overline">Deployment</div>
      <h1 className="font-display text-3xl font-extrabold mt-2 text-navy">Deployment Package</h1>
      <p className="text-sm text-ink-muted mt-2">
        One-click download of the entire site (backend + frontend + schema). Re-generates
        a fresh ZIP every time, so the file you download always reflects the latest code
        on this server.
      </p>

      <div className="mt-8 card-flat p-7" data-testid="deployment-card">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-2xl bg-saffron/15 text-saffron grid place-items-center shrink-0">
            <Download className="h-6 w-6"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-lg text-navy">Full TARA Finserv deployment</div>
            <div className="text-sm text-ink-muted mt-1">
              Includes: <code className="text-xs">app.js</code>, <code className="text-xs">db.js</code>,{" "}
              <code className="text-xs">defaults.js</code>, <code className="text-xs">package.json</code>,{" "}
              <code className="text-xs">schema.sql</code>, <code className="text-xs">DEPLOY-CPANEL-NODE.txt</code>{" "}
              and the entire <code className="text-xs">public/</code> folder.
            </div>
            <div className="text-xs text-ink-muted mt-3 flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-green-600"/>
              Your <code className="text-[10px]">.env</code> file (with DB password) is{" "}
              <strong>excluded</strong> for security. <code className="text-[10px]">.env.example</code> is included.
            </div>
            <Button
              data-testid="download-deployment-btn"
              onClick={handleDownload}
              disabled={downloading}
              className="btn-saffron rounded-full h-11 px-7 mt-5"
            >
              {downloading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin"/> Building ZIP…</>
              ) : (
                <><Download className="h-4 w-4 mr-2"/> Download deployment ZIP</>
              )}
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <div className="card-flat p-5">
          <div className="flex items-center gap-2 mb-2">
            <RotateCcw className="h-4 w-4 text-saffron"/>
            <div className="font-display font-bold text-navy">Always up-to-date</div>
          </div>
          <p className="text-sm text-ink-muted">
            The ZIP is built on every download from the live files on this server.
            If we ever push a frontend or backend update, your next download
            automatically includes it.
          </p>
        </div>
        <div className="card-flat p-5">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="h-4 w-4 text-saffron"/>
            <div className="font-display font-bold text-navy">Safe to share</div>
          </div>
          <p className="text-sm text-ink-muted">
            No secrets are in the package. The <code className="text-xs">.env</code> file
            (containing DB password) is automatically skipped. Use this as a long-term
            code backup or to re-deploy on another server.
          </p>
        </div>
      </div>

      <div className="mt-8 card-flat p-6 bg-slate-50">
        <div className="font-display font-bold text-navy mb-2">How to use the ZIP</div>
        <ol className="text-sm text-ink-muted list-decimal pl-5 space-y-1.5">
          <li>Save the downloaded ZIP somewhere safe (Google Drive, your computer).</li>
          <li>If you ever need to redeploy: follow the steps in <code className="text-xs">DEPLOY-CPANEL-NODE.txt</code> inside the ZIP.</li>
          <li>You'll need: a MySQL database, your DB credentials, and Node.js 18+ on cPanel.</li>
          <li>The data inside MySQL is NOT part of this ZIP — back that up separately via phpMyAdmin → Export.</li>
        </ol>
      </div>

      <div className="mt-3 text-xs text-ink-muted">
        Endpoint: <code className="text-[10px]">{API_BASE}/admin/deployment-package</code>
      </div>
    </div>
  );
}
